import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { type Event } from 'nostr-tools';
import {
  CalendarDays, Plus, Search, X, Loader2, Image as ImageIcon, MapPin,
  Clock, Users, Send, Heart, MessageCircle, Camera, Share2, ArrowLeft,
  Check, AlertCircle, QrCode, Copy, ExternalLink, ChevronDown,
  User, Trash2, Edit3, Filter,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { uploadImage } from '../lib/imageUpload';
import { timeAgo } from '../lib/utils';

// ── NIP-52 Calendar Event Kinds ─────────────────────────────────────
const DATE_EVENT_KIND = 31922;
const TIME_EVENT_KIND = 31923;
const RSVP_KIND = 31925;
const EVENT_KINDS = [DATE_EVENT_KIND, TIME_EVENT_KIND];

// BCH community tags
const BCH_TAGS = ['bch', 'bitcoincash', 'bchnostr', 'bchevent'];

// ── Helpers ─────────────────────────────────────────────────────────

interface CalendarEvent {
  event: Event;
  title: string;
  description: string;
  image?: string;
  location?: string;
  startDate: Date;
  endDate?: Date;
  isDateBased: boolean;
  pubkey: string;
  dTag: string;
  geohash?: string;
  participants: { pubkey: string; role?: string }[];
  hashtags: string[];
}

function parseCalendarEvent(event: Event): CalendarEvent | null {
  const tags = event.tags;
  const title = tags.find(t => t[0] === 'title')?.[1] || tags.find(t => t[0] === 'name')?.[1];
  if (!title) return null;

  const isDateBased = event.kind === DATE_EVENT_KIND;
  const startRaw = tags.find(t => t[0] === 'start')?.[1];
  if (!startRaw) return null;

  let startDate: Date;
  let endDate: Date | undefined;

  if (isDateBased) {
    startDate = new Date(startRaw + 'T00:00:00');
    const endRaw = tags.find(t => t[0] === 'end')?.[1];
    if (endRaw) endDate = new Date(endRaw + 'T00:00:00');
  } else {
    startDate = new Date(parseInt(startRaw) * 1000);
    const endRaw = tags.find(t => t[0] === 'end')?.[1];
    if (endRaw) endDate = new Date(parseInt(endRaw) * 1000);
  }

  if (isNaN(startDate.getTime())) return null;

  const image = tags.find(t => t[0] === 'image')?.[1];
  const location = tags.find(t => t[0] === 'location')?.[1];
  const geohash = tags.find(t => t[0] === 'g')?.[1];
  const description = event.content || tags.find(t => t[0] === 'summary')?.[1] || '';

  const participants: { pubkey: string; role?: string }[] = [];
  for (const tag of tags) {
    if (tag[0] === 'p' && tag[1]) {
      participants.push({ pubkey: tag[1], role: tag[3] });
    }
  }

  const hashtags: string[] = [];
  for (const tag of tags) {
    if (tag[0] === 't' && tag[1]) hashtags.push(tag[1].toLowerCase());
  }

  const dTag = tags.find(t => t[0] === 'd')?.[1] || '';

  return {
    event, title, description, image, location, startDate, endDate,
    isDateBased, pubkey: event.pubkey, dTag, geohash, participants, hashtags,
  };
}

function formatEventDate(d: Date, isDateBased: boolean): string {
  if (isDateBased) {
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatEventDateShort(d: Date): string {
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (days < 0) return 'Past';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 7) return `In ${days} days`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function isUpcoming(ev: CalendarEvent): boolean {
  const now = new Date();
  const end = ev.endDate || ev.startDate;
  return end >= now;
}

function isPast(ev: CalendarEvent): boolean {
  return !isUpcoming(ev);
}

function generateEventId(): string {
  return crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '').slice(0, 16) :
    Array.from(crypto.getRandomValues(new Uint8Array(8))).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── QR Code generation (simple SVG) ─────────────────────────────────
// Lightweight QR code using a basic encoding approach
function generateQRCodeSVG(text: string, size: number = 200): string {
  // Use a simple QR code API via image tag instead of generating SVG
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=ffffff&color=000000&format=svg`;
}

// ── Main Page ───────────────────────────────────────────────────────

export default function EventsPage() {
  const { user, setShowLoginDialog } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewEventId = searchParams.get('event');
  const viewPubkey = searchParams.get('by');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState<'upcoming' | 'past' | 'mine'>('upcoming');

  // Fetch calendar events — only BCH-tagged
  const { events: calendarEvents, loading } = useNostrQuery({
    filters: [
      { kinds: EVENT_KINDS, '#t': BCH_TAGS, limit: 200 },
    ],
    allRelays: true,
  });

  const parsedEvents = useMemo(() => {
    const seen = new Set<string>();
    const result: CalendarEvent[] = [];
    for (const ev of calendarEvents) {
      const key = `${ev.pubkey}:${ev.tags.find(t => t[0] === 'd')?.[1] || ev.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const parsed = parseCalendarEvent(ev);
      if (parsed) result.push(parsed);
    }
    return result.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [calendarEvents]);

  const filteredEvents = useMemo(() => {
    let events = parsedEvents;

    // Tab filter
    if (tab === 'upcoming') events = events.filter(isUpcoming);
    else if (tab === 'past') events = events.filter(isPast).reverse();
    else if (tab === 'mine') events = events.filter(e => e.pubkey === user?.pubkey);

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      events = events.filter(e =>
        e.title.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.location?.toLowerCase().includes(q) ||
        e.hashtags.some(t => t.includes(q))
      );
    }

    return events;
  }, [parsedEvents, tab, search, user?.pubkey]);

  // If viewing a specific event
  if (viewEventId && viewPubkey) {
    const event = parsedEvents.find(e => e.dTag === viewEventId && e.pubkey === viewPubkey);
    return (
      <EventDetailView
        calEvent={event || null}
        dTag={viewEventId}
        pubkey={viewPubkey}
        onBack={() => setSearchParams({})}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-20">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-primary" />
            Events
          </h1>
          <button
            onClick={() => {
              if (!user) { setShowLoginDialog(true); return; }
              setShowCreate(true);
            }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Event
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search events..."
            className="w-full pl-10 pr-8 py-2 rounded-full bg-muted text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {(['upcoming', 'past', 'mine'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                tab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {t === 'upcoming' ? 'Upcoming' : t === 'past' ? 'Past' : 'My Events'}
            </button>
          ))}
        </div>
      </div>

      {/* Event List */}
      <div className="px-4 py-4 space-y-3">
        {loading && filteredEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 text-primary/30 animate-spin" />
            <p className="text-sm text-muted-foreground">Loading events...</p>
          </div>
        )}

        {!loading && filteredEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <CalendarDays className="w-12 h-12 text-muted-foreground/20" />
            <p className="text-sm font-medium text-muted-foreground">
              {tab === 'mine' ? 'You haven\'t created any events yet' :
               tab === 'past' ? 'No past events found' :
               search ? 'No events match your search' : 'No upcoming events'}
            </p>
            {tab !== 'past' && (
              <button
                onClick={() => {
                  if (!user) { setShowLoginDialog(true); return; }
                  setShowCreate(true);
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> Create the first event
              </button>
            )}
          </div>
        )}

        {filteredEvents.map(ev => (
          <EventCard
            key={`${ev.pubkey}:${ev.dTag}`}
            calEvent={ev}
            onClick={() => setSearchParams({ event: ev.dTag, by: ev.pubkey })}
          />
        ))}
      </div>

      {/* Create Event Modal */}
      {showCreate && <CreateEventModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

// ── Event Card ──────────────────────────────────────────────────────

function EventCard({ calEvent, onClick }: { calEvent: CalendarEvent; onClick: () => void }) {
  const { profile } = useNostrProfile(calEvent.pubkey);
  const name = profile?.display_name || profile?.name || calEvent.pubkey.slice(0, 8) + '...';
  const upcoming = isUpcoming(calEvent);
  const dateLabel = formatEventDateShort(calEvent.startDate);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border border-border bg-card hover:bg-muted/50 transition-all overflow-hidden group"
    >
      {/* Cover image */}
      {calEvent.image && (
        <div className="relative h-40 overflow-hidden">
          <img
            src={calEvent.image}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-3 left-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
              upcoming ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/80 text-white'
            }`}>
              {dateLabel}
            </span>
          </div>
        </div>
      )}

      <div className="p-4">
        {/* Date badge (when no image) */}
        {!calEvent.image && (
          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold mb-2 ${
            upcoming ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
          }`}>
            {dateLabel}
          </span>
        )}

        <h3 className="font-bold text-base mb-1 group-hover:text-primary transition-colors line-clamp-2">
          {calEvent.title}
        </h3>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatEventDate(calEvent.startDate, calEvent.isDateBased)}
          </span>
        </div>

        {calEvent.location && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{calEvent.location}</span>
          </p>
        )}

        {calEvent.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{calEvent.description}</p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-3 h-3 text-primary/60" />
              </div>
            )}
            <span className="text-xs text-muted-foreground">{name}</span>
          </div>
          {calEvent.participants.length > 0 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> {calEvent.participants.length}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Event Detail View ───────────────────────────────────────────────

function EventDetailView({ calEvent, dTag, pubkey, onBack }: {
  calEvent: CalendarEvent | null;
  dTag: string;
  pubkey: string;
  onBack: () => void;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile: organizerProfile } = useNostrProfile(pubkey);
  const [showChat, setShowChat] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState<'none' | 'accepted' | 'declined' | 'tentative'>('none');
  const [rsvpSending, setRsvpSending] = useState(false);

  // Fetch RSVPs for this event
  const eventCoord = `${calEvent?.event.kind || TIME_EVENT_KIND}:${pubkey}:${dTag}`;
  const { events: rsvpEvents } = useNostrQuery({
    filters: [{ kinds: [RSVP_KIND], '#a': [eventCoord], limit: 200 }],
    enabled: !!calEvent,
    allRelays: true,
  });

  // Fetch media posts tagged to this event
  const { events: mediaPosts, loading: mediaLoading } = useNostrQuery({
    filters: [{ kinds: [1], '#a': [eventCoord], limit: 200 }],
    enabled: !!calEvent,
    allRelays: true,
  });

  const rsvps = useMemo(() => {
    const map = new Map<string, { status: string; pubkey: string }>();
    for (const ev of rsvpEvents) {
      const status = ev.tags.find(t => t[0] === 'status')?.[1];
      if (status) map.set(ev.pubkey, { status, pubkey: ev.pubkey });
    }
    return [...map.values()];
  }, [rsvpEvents]);

  const attendingCount = rsvps.filter(r => r.status === 'accepted').length;

  // Check if current user has RSVP'd
  useEffect(() => {
    if (!user) return;
    const myRsvp = rsvps.find(r => r.pubkey === user.pubkey);
    if (myRsvp) setRsvpStatus(myRsvp.status as any);
  }, [rsvps, user?.pubkey]);

  const handleRsvp = async (status: 'accepted' | 'declined' | 'tentative') => {
    if (!user) { setShowLoginDialog(true); return; }
    if (rsvpSending) return;
    setRsvpSending(true);
    try {
      await publish({
        kind: RSVP_KIND,
        content: '',
        tags: [
          ['a', eventCoord],
          ['d', `rsvp-${dTag}`],
          ['status', status],
          ['p', pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setRsvpStatus(status);
    } catch (e) {
      console.error('RSVP failed:', e);
    }
    setRsvpSending(false);
  };

  const shareUrl = `${window.location.origin}/events?event=${dTag}&by=${pubkey}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {}
  };

  if (!calEvent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Events
        </button>
        <div className="text-center py-16">
          <CalendarDays className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Event not found or still loading...</p>
        </div>
      </div>
    );
  }

  const organizerName = organizerProfile?.display_name || organizerProfile?.name || pubkey.slice(0, 8) + '...';

  return (
    <div className="max-w-2xl mx-auto pb-20">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-base truncate flex-1">{calEvent.title}</h1>
        <button onClick={() => setShowShare(true)} className="p-1.5 rounded-lg hover:bg-muted">
          <Share2 className="w-5 h-5" />
        </button>
      </div>

      {/* Cover image */}
      {calEvent.image && (
        <div className="relative h-52 sm:h-64 overflow-hidden">
          <img src={calEvent.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      )}

      {/* Event info */}
      <div className="px-4 py-4 space-y-4">
        <div>
          <h2 className="text-2xl font-bold mb-2">{calEvent.title}</h2>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-primary shrink-0" />
              <span>{formatEventDate(calEvent.startDate, calEvent.isDateBased)}</span>
              {calEvent.endDate && (
                <span className="text-muted-foreground">
                  — {formatEventDate(calEvent.endDate, calEvent.isDateBased)}
                </span>
              )}
            </div>

            {calEvent.location && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <span>{calEvent.location}</span>
              </div>
            )}

            <Link to={`/profile/${pubkey}`} className="flex items-center gap-2 text-sm hover:underline">
              {organizerProfile?.picture ? (
                <img src={organizerProfile.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-3 h-3 text-primary/60" />
                </div>
              )}
              <span className="text-muted-foreground">Organized by <strong className="text-foreground">{organizerName}</strong></span>
            </Link>
          </div>
        </div>

        {/* RSVP */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 flex-1">
            {(['accepted', 'tentative', 'declined'] as const).map(status => (
              <button
                key={status}
                onClick={() => handleRsvp(status)}
                disabled={rsvpSending}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                  rsvpStatus === status
                    ? status === 'accepted' ? 'bg-emerald-500 text-white'
                    : status === 'tentative' ? 'bg-amber-500 text-white'
                    : 'bg-red-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {status === 'accepted' ? 'Going' : status === 'tentative' ? 'Maybe' : 'Can\'t Go'}
              </button>
            ))}
          </div>
        </div>

        {attendingCount > 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {attendingCount} {attendingCount === 1 ? 'person' : 'people'} going
          </p>
        )}

        {/* Description */}
        {calEvent.description && (
          <div className="border-t border-border pt-4">
            <p className="text-sm whitespace-pre-wrap">{calEvent.description}</p>
          </div>
        )}

        {/* Hashtags */}
        {calEvent.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {calEvent.hashtags.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Tabs: Feed & Chat */}
        <div className="border-t border-border pt-4">
          <div className="flex gap-1 mb-4">
            <button
              onClick={() => setShowChat(true)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
                showChat ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              Feed & Chat
            </button>
            <button
              onClick={() => setShowChat(false)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
                !showChat ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              Attendees ({rsvps.filter(r => r.status === 'accepted').length})
            </button>
          </div>

          {showChat ? (
            <EventFeed eventCoord={eventCoord} calEvent={calEvent} mediaPosts={mediaPosts} mediaLoading={mediaLoading} />
          ) : (
            <AttendeesList rsvps={rsvps} />
          )}
        </div>
      </div>

      {/* Share Sheet */}
      {showShare && (
        <ShareSheet
          url={shareUrl}
          title={calEvent.title}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}

// ── Event Feed (media + comments) ───────────────────────────────────

function EventFeed({ eventCoord, calEvent, mediaPosts, mediaLoading }: {
  eventCoord: string;
  calEvent: CalendarEvent;
  mediaPosts: Event[];
  mediaLoading: boolean;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localPosts, setLocalPosts] = useState<Event[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const allPosts = useMemo(() => {
    const seen = new Set<string>();
    return [...mediaPosts, ...localPosts]
      .filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true; })
      .sort((a, b) => a.created_at - b.created_at);
  }, [mediaPosts, localPosts]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [allPosts.length]);

  const handleSend = async () => {
    if (!user || (!content.trim()) || sending) return;
    setSending(true);
    try {
      const now = Math.floor(Date.now() / 1000);
      const tags: string[][] = [
        ['a', eventCoord, '', 'root'],
        ['p', calEvent.pubkey],
        ['t', 'bch'],
        ['t', 'bchevent'],
      ];
      const event = { kind: 1, content: content.trim(), tags, created_at: now };
      await publish(event);
      setLocalPosts(prev => [...prev, {
        id: 'local-' + Date.now(), pubkey: user.pubkey,
        content: content.trim(), created_at: now, tags, kind: 1, sig: '',
      } as any]);
      setContent('');
    } catch (e) { console.error('Post failed:', e); }
    setSending(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      const now = Math.floor(Date.now() / 1000);
      const imageContent = content.trim() ? `${content.trim()}\n\n${url}` : url;
      const tags: string[][] = [
        ['a', eventCoord, '', 'root'],
        ['p', calEvent.pubkey],
        ['t', 'bch'],
        ['t', 'bchevent'],
      ];
      const event = { kind: 1, content: imageContent, tags, created_at: now };
      await publish(event);
      setLocalPosts(prev => [...prev, {
        id: 'local-' + Date.now(), pubkey: user.pubkey,
        content: imageContent, created_at: now, tags, kind: 1, sig: '',
      } as any]);
      setContent('');
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      {/* Posts */}
      <div ref={scrollRef} className="space-y-3 max-h-[60vh] overflow-y-auto mb-4">
        {mediaLoading && allPosts.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-primary/30 animate-spin" />
          </div>
        )}

        {!mediaLoading && allPosts.length === 0 && (
          <div className="text-center py-8">
            <Camera className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
            <p className="text-sm text-muted-foreground">No posts yet</p>
            <p className="text-xs text-muted-foreground mt-0.5">Share a photo or comment!</p>
          </div>
        )}

        {allPosts.map(post => (
          <FeedPost key={post.id} event={post} />
        ))}
      </div>

      {/* Post input */}
      {user ? (
        <div className="border-t border-border pt-3">
          <div className="flex items-center gap-2">
            <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-2.5 rounded-full hover:bg-muted text-muted-foreground transition-colors shrink-0"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            </button>
            <input
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Share a photo or comment..."
              className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            <button
              onClick={handleSend}
              disabled={!content.trim() || sending}
              className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 transition-colors shrink-0"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-border pt-3 text-center">
          <p className="text-xs text-muted-foreground">Log in to post</p>
        </div>
      )}
    </div>
  );
}

// ── Feed Post ───────────────────────────────────────────────────────

function FeedPost({ event }: { event: Event }) {
  const { profile } = useNostrProfile(event.pubkey);
  const name = profile?.display_name || profile?.name || event.pubkey.slice(0, 8) + '...';

  // Extract image URLs from content
  const imageUrls = useMemo(() => {
    const urls: string[] = [];
    const matches = event.content.matchAll(/(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|avif)(\?[^\s]*)?)/gi);
    for (const m of matches) urls.push(m[1]);
    return urls;
  }, [event.content]);

  const textContent = event.content.replace(/(https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp|avif)(\?[^\s]*)?)/gi, '').trim();

  return (
    <div className="flex gap-2.5">
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary/60" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <Link to={`/profile/${event.pubkey}`} className="text-xs font-semibold hover:underline truncate">
            {name}
          </Link>
          <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(event.created_at)}</span>
        </div>

        {textContent && (
          <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{textContent}</p>
        )}

        {imageUrls.length > 0 && (
          <div className={`mt-2 grid gap-1.5 ${imageUrls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {imageUrls.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block rounded-lg overflow-hidden">
                <img src={url} alt="" className="w-full h-auto max-h-64 object-cover rounded-lg" loading="lazy" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Attendees List ──────────────────────────────────────────────────

function AttendeesList({ rsvps }: { rsvps: { status: string; pubkey: string }[] }) {
  const accepted = rsvps.filter(r => r.status === 'accepted');
  const tentative = rsvps.filter(r => r.status === 'tentative');

  if (accepted.length === 0 && tentative.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
        <p className="text-sm text-muted-foreground">No RSVPs yet</p>
        <p className="text-xs text-muted-foreground mt-0.5">Be the first to RSVP!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {accepted.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Going ({accepted.length})
          </h4>
          <div className="space-y-2">
            {accepted.map(r => <AttendeeRow key={r.pubkey} pubkey={r.pubkey} />)}
          </div>
        </div>
      )}
      {tentative.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Maybe ({tentative.length})
          </h4>
          <div className="space-y-2">
            {tentative.map(r => <AttendeeRow key={r.pubkey} pubkey={r.pubkey} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function AttendeeRow({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  const name = profile?.display_name || profile?.name || pubkey.slice(0, 8) + '...';

  return (
    <Link to={`/profile/${pubkey}`} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted transition-colors">
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="w-4 h-4 text-primary/60" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{name}</p>
        {profile?.about && <p className="text-xs text-muted-foreground truncate">{profile.about}</p>}
      </div>
    </Link>
  );
}

// ── Share Sheet ──────────────────────────────────────────────────────

function ShareSheet({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        onClose();
      } catch {}
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        onClick={e => e.stopPropagation()}
        className="relative w-full max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-5 animate-[slideUp_0.2s_ease-out]"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base">Share Event</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* QR Code */}
        <div className="flex justify-center mb-4">
          <img
            src={generateQRCodeSVG(url, 180)}
            alt="QR Code"
            className="w-44 h-44 rounded-xl bg-white p-2"
          />
        </div>

        {/* Link */}
        <div className="flex items-center gap-2 bg-muted rounded-lg p-2 mb-3">
          <p className="flex-1 text-xs text-muted-foreground truncate font-mono">{url}</p>
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>

        {navigator.share && (
          <button
            onClick={handleNativeShare}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        )}
      </div>
    </div>
  );
}

// ── Create Event Modal ──────────────────────────────────────────────

function CreateEventModal({ onClose }: { onClose: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [error, setError] = useState('');
  const [hashtags, setHashtags] = useState('bch, bitcoincash');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadImage(file);
      setImageUrl(url);
    } catch (err) {
      setError('Image upload failed. Try again.');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePublish = async () => {
    if (!user || publishing) return;
    setError('');

    if (!title.trim()) { setError('Title is required'); return; }
    if (!startDate) { setError('Start date is required'); return; }
    if (!isAllDay && !startTime) { setError('Start time is required for timed events'); return; }

    setPublishing(true);
    try {
      const dTag = generateEventId();
      const now = Math.floor(Date.now() / 1000);
      const tags: string[][] = [
        ['d', dTag],
        ['title', title.trim()],
      ];

      if (description.trim()) tags.push(['summary', description.trim()]);
      if (imageUrl) tags.push(['image', imageUrl]);
      if (location.trim()) tags.push(['location', location.trim()]);

      // Parse hashtags
      const tagList = hashtags.split(',').map(t => t.trim().toLowerCase().replace(/^#/, '')).filter(Boolean);
      // Always include BCH tags
      const allTags = new Set([...tagList, 'bch', 'bchevent']);
      for (const t of allTags) tags.push(['t', t]);

      let kind: number;
      if (isAllDay) {
        kind = DATE_EVENT_KIND;
        tags.push(['start', startDate]);
        if (endDate) tags.push(['end', endDate]);
      } else {
        kind = TIME_EVENT_KIND;
        const startTs = Math.floor(new Date(`${startDate}T${startTime}`).getTime() / 1000);
        tags.push(['start', String(startTs)]);

        // Add D tag (day-granularity timestamp)
        const dayTs = Math.floor(startTs / 86400);
        tags.push(['D', String(dayTs)]);

        if (endDate && endTime) {
          const endTs = Math.floor(new Date(`${endDate}T${endTime}`).getTime() / 1000);
          tags.push(['end', String(endTs)]);

          // Add D tags for multi-day events
          const endDayTs = Math.floor(endTs / 86400);
          if (endDayTs > dayTs) {
            for (let d = dayTs + 1; d <= endDayTs; d++) {
              tags.push(['D', String(d)]);
            }
          }
        }

        // Detect timezone
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) tags.push(['start_tzid', tz]);
        } catch {}
      }

      await publish({
        kind,
        content: description.trim(),
        tags,
        created_at: now,
      });

      setPublished(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError('Failed to publish event. Try again.');
      console.error('Publish error:', err);
    }
    setPublishing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        onClick={e => e.stopPropagation()}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-card border border-border rounded-t-2xl sm:rounded-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-card/95 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between z-10">
          <h2 className="font-bold text-base">Create Event</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {published ? (
          <div className="flex flex-col items-center gap-3 py-12 px-4">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-lg font-bold">Event Created!</p>
            <p className="text-sm text-muted-foreground">Your event is now live.</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Cover image */}
            <div>
              <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
              {imageUrl ? (
                <div className="relative rounded-xl overflow-hidden h-40">
                  <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setImageUrl('')}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-2 text-muted-foreground transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <ImageIcon className="w-6 h-6" />
                      <span className="text-xs">Add cover image</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Event Title *</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="BCH Meetup, Birthday Party, Concert..."
                className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Tell people about your event..."
                rows={3}
                className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                maxLength={2000}
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="Address, venue, or online link"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-muted text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* All day toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAllDay}
                onChange={e => setIsAllDay(e.target.checked)}
                className="w-4 h-4 rounded border-border accent-primary"
              />
              <span className="text-sm">All-day event</span>
            </label>

            {/* Date/time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Start Date *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              {!isAllDay && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Start Time *</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              {!isAllDay && (
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">End Time</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}
            </div>

            {/* Hashtags */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Tags (comma separated)</label>
              <input
                value={hashtags}
                onChange={e => setHashtags(e.target.value)}
                placeholder="bch, meetup, conference"
                className="w-full px-3 py-2.5 rounded-lg bg-muted text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 text-red-500 text-xs">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handlePublish}
              disabled={publishing || !title.trim() || !startDate}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-colors hover:bg-primary/90"
            >
              {publishing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
              ) : (
                <><CalendarDays className="w-4 h-4" /> Create Event</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

